import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { Currency } from 'lumen/models';
import type CurrencyConversionExchangeRateModel from 'lumen/models/currency-conversion/exchange-rate';
import dirtyRelationship, {
  type DirtyRelationshipModel,
} from 'lumen/utils/decorators/dirty-relationship';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface CurrencyConversionExchangeRatesProfileModel
  extends DirtyRelationshipModel, Model {}

@dirtyRelationship()
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class CurrencyConversionExchangeRatesProfileModel extends Model {
  [Type] = 'currency-conversion/exchange-rates-profile' as const;

  @belongsTo('currency', { async: false, inverse: null })
  declare baseCurrency: Currency;

  @hasMany('currency-conversion/exchange-rate', {
    async: false,
    inverse: 'exchangeRatesProfile',
  })
  declare rates: HasMany<CurrencyConversionExchangeRateModel>;

  @attr('dayjs') declare createdAt: NonNullable<DayjsTransformReturn>;
  @attr('boolean') declare isSynced: boolean;
  @attr('string') declare title: string;
  @attr('dayjs') declare updatedAt: NonNullable<DayjsTransformReturn>;

  @service declare currencyConversion: Services['currency-conversion'];

  get createdAtFromNow(): string {
    return this.createdAt.fromNow();
  }

  get hasInvalidRates(): boolean {
    return this.rates.some((rate) => !rate.isValid);
  }

  get hasNoExchangeRates(): boolean {
    return (
      this.rates.filter((rate) => !rate.isDeleted && !rate.isNew).length === 0
    );
  }

  get hasUnsavedChanges(): boolean {
    const hasProfileChanged =
      this.hasDirtyAttributes || this.hasDirtyRelationships;

    const rates = this.rates;

    const hasRatesChanged = rates.some(
      (rate) => rate.hasDirtyAttributes || rate.isNew || rate.isDeleted,
    );

    return hasProfileChanged || hasRatesChanged;
  }

  get isMissingAllPortfolioCurrencies(): boolean {
    const baseCurrencyCode = this.baseCurrency.code;
    const portfolioCurrencies =
      this.currencyConversion.portfolioCurrencies.filter(
        (currency) => currency.code !== baseCurrencyCode,
      );

    if (portfolioCurrencies.length === 0) {
      return false;
    }

    const availableRateCodes = new Set(
      this.rates
        .filter((rate) => !rate.isDeleted && !rate.isNew)
        .map((rate) => rate.currency?.code),
    );

    return portfolioCurrencies.every(
      (currency) => !availableRateCodes.has(currency.code),
    );
  }

  get isMissingPortfolioCurrencies(): boolean {
    const portfolioCurrencies = this.currencyConversion.portfolioCurrencies;
    const baseCurrencyCode = this.baseCurrency.code;

    const availableRateCodes = new Set(
      this.rates
        .filter((rate) => !rate.isDeleted && !rate.isNew)
        .map((rate) => rate.currency?.code),
    );

    return portfolioCurrencies
      .filter((currency) => currency.code !== baseCurrencyCode)
      .some((currency) => !availableRateCodes.has(currency.code));
  }

  get isValid(): boolean {
    return (
      this.isSynced &&
      this.rates.length > 0 &&
      this.rates.every((rate) => rate.isValid)
    );
  }

  get updatedAtFromNow(): string {
    return this.updatedAt.fromNow();
  }
}

export default CurrencyConversionExchangeRatesProfileModel;
